/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useDestinationOptions.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    Custom hook to fetch destination options dynamically from API
    based on the selected module type.

 🔧 Notes:
    - Fetches destinations from API for each module type
    - Caches results to avoid unnecessary API calls
    - Handles loading and error states
    - Returns empty array for unsupported modules

 🔁 Last Updated: 2025-01-28 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback } from 'react';
import { buildApiUrl, getApiHeaders } from '../config/api';

export interface DestinationOption {
  value: string;
  label: string;
  tooltip?: string;
  description?: string;
}

// Cache for destination options to avoid repeated API calls
const destinationCache = new Map<string, DestinationOption[]>();
const loadingPromises = new Map<string, Promise<DestinationOption[]>>();

/**
 * Resolve a human-readable label for a module/value destination pair.
 * Uses the in-memory cache populated by useDestinationOptions.
 * Falls back to the raw value if not found in cache.
 */
export function resolveDestinationLabel(module: string, value: string): string | null {
  if (!module || !value) return null;
  const cached = destinationCache.get(module);
  if (cached) {
    const match = cached.find((opt) => opt.value === value);
    if (match) return match.label;
  }
  return value; // fallback to raw value
}

// Module to API endpoint mapping
const MODULE_API_MAP: Record<string, { endpoint: string; valueField: string; labelField: string; responseField?: string; enabledField?: string; extraParams?: Record<string, string> }> = {
  extension: {
    endpoint: '/api/telephony/extensions',
    valueField: 'extension',
    labelField: 'name',
    enabledField: 'enabled'
  },
  conference: {
    endpoint: '/api/telephony/conferences',
    valueField: 'extension',
    labelField: 'name',
    enabledField: 'enabled'
  },
  direct_dial: {
    endpoint: '/api/telephony/direct-dial',
    valueField: 'code',
    labelField: 'name',
    enabledField: 'enabled'
  },
  direct_route: {
    endpoint: '/api/telephony/direct-route',
    valueField: 'context', // Direct routes use context as identifier
    labelField: 'name',
    enabledField: 'enabled'
  },
  paging: {
    endpoint: '/api/telephony/paging-groups',
    valueField: 'code',
    labelField: 'name',
    enabledField: 'enabled'
  },
  parking: {
    endpoint: '/api/telephony/parking-lots',
    valueField: 'extension',
    labelField: 'name',
    enabledField: 'enabled'
  },
  disa: {
    endpoint: '/api/telephony/disa-entries',
    valueField: 'context',
    labelField: 'name',
    enabledField: 'enabled'
  },
  ring_group: {
    endpoint: '/api/telephony/ring-groups',
    valueField: 'context', // Ring groups use context as identifier
    labelField: 'name',
    enabledField: 'enabled'
  },
  queue: {
    endpoint: '/api/telephony/queues',
    valueField: 'extension',
    labelField: 'name',
    enabledField: 'enabled'
  },
  ivr: {
    endpoint: '/api/telephony/ivrs',
    valueField: 'context',
    labelField: 'name',
    enabledField: 'enabled'
  },
  time_condition: {
    endpoint: '/api/telephony/time-conditions',
    valueField: 'name',
    labelField: 'name',
    enabledField: 'enabled'
  },
  announcement: {
    endpoint: '/api/telephony/announcements',
    valueField: 'context',
    labelField: 'name',
    enabledField: 'enabled'
  },
  language: {
    endpoint: '/api/telephony/languages',
    valueField: 'context',
    labelField: 'name',
    enabledField: 'enabled'
  },
  call_flow: {
    endpoint: '/api/telephony/call-flows',
    valueField: 'context',
    labelField: 'name',
    enabledField: 'enabled'
  },
  authentication_code: {
    endpoint: '/api/telephony/authentication-rules',
    valueField: 'context',
    labelField: 'name',
    enabledField: 'enabled'
  },
  ai_agent: {
    endpoint: '/api/telephony/ai-agents',
    valueField: 'extension',
    labelField: 'agent_name',
    responseField: 'agentName',
    enabledField: 'enabled'
  },
  callback_rules: {
    endpoint: '/api/telephony/callback-rules',
    valueField: 'uuid',       // Lua looks up by UUID
    labelField: 'name',
    enabledField: 'enabled'
  },
  custom_application: {
    endpoint: '/api/telephony/custom-applications',
    valueField: 'uuid',       // Invoked by UUID via Direct Dial
    labelField: 'name',
    enabledField: 'enabled'
  },
  voicemail: {
    // Extensiones con voicemail activo — enruta directo al buzón
    endpoint: '/api/telephony/extensions',
    valueField: 'extension',
    labelField: 'name',
    enabledField: 'enabled',
    extraParams: { voicemailEnabled: 'true' }
  },
  fax: {
    // Extensiones con fax activo (campo fax_enabled en sip_extensions)
    endpoint: '/api/telephony/extensions',
    valueField: 'extension',
    labelField: 'name',
    enabledField: 'enabled',
    extraParams: { faxEnabled: 'true' }
  }
};

export const useDestinationOptions = (moduleType: string) => {
  const [destinations, setDestinations] = useState<DestinationOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDestinations = useCallback(async (module: string): Promise<DestinationOption[]> => {
    // Check cache first
    if (destinationCache.has(module)) {
      return destinationCache.get(module)!;
    }

    // Check if there's already a loading promise for this module
    if (loadingPromises.has(module)) {
      return loadingPromises.get(module)!;
    }

    // If module is terminate_call, return empty (handled separately)
    if (module === 'terminate_call') {
      return [];
    }

    // Get API config for this module
    const apiConfig = MODULE_API_MAP[module];
    if (!apiConfig) {
      return [];
    }

    // Create loading promise
    const loadPromise = (async () => {
      try {
        // Fetch with high limit to get all enabled items
        const queryParams = new URLSearchParams({
          page: '1',
          limit: '1000', // High limit to get all items
          sortBy: apiConfig.labelField,
          sortOrder: 'asc',
          ...(apiConfig.extraParams || {})
        });

        const response = await fetch(
          buildApiUrl(`${apiConfig.endpoint}?${queryParams}`),
          {
            headers: getApiHeaders()
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch destinations for ${module}`);
        }

        const result = await response.json();

        if (!result?.success || !Array.isArray(result.data)) {
          return [];
        }

        // Transform API response to DestinationOption format
        const options: DestinationOption[] = result.data
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((item: any) => {
            // Filter by enabled if enabledField is specified
            if (apiConfig.enabledField && item[apiConfig.enabledField] === false) {
              return false;
            }
            // Ensure required fields exist (use responseField if available for reading response data)
            const labelKey = apiConfig.responseField || apiConfig.labelField;
            return item[apiConfig.valueField] && item[labelKey];
          })
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((item: any) => {
            const labelKey = apiConfig.responseField || apiConfig.labelField;
            const value = String(item[apiConfig.valueField]);
            const label = String(item[labelKey]);

            // Build display label with extension/context/code if available
            let displayLabel = label;
            if (value && (apiConfig.valueField === 'extension' || apiConfig.valueField === 'context' || apiConfig.valueField === 'code')) {
              // Only append if value is different from label
              if (value !== label) {
                displayLabel = `${label} (${value})`;
              }
            }

            return {
              value,
              label: displayLabel,
              description: item.description || undefined
            };
          });

        // Cache the results
        destinationCache.set(module, options);
        return options;
      } catch (err) {
        console.error(`Error fetching destinations for ${module}:`, err);
        return [];
      } finally {
        // Remove loading promise
        loadingPromises.delete(module);
      }
    })();

    // Store loading promise
    loadingPromises.set(module, loadPromise);

    return loadPromise;
  }, []);

  useEffect(() => {
    if (!moduleType) {
      setDestinations([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    fetchDestinations(moduleType)
      .then((options) => {
        setDestinations(options);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error loading destinations:', err);
        setError('Failed to load destinations');
        setDestinations([]);
        setIsLoading(false);
      });
  }, [moduleType, fetchDestinations]);

  return {
    destinations,
    isLoading,
    error
  };
};

